import ReactYouTube from 'react-player/youtube';
import { useEffect, useState } from 'react';
import { Button } from '~components/Button';
import { ComponentLink } from '~components/ComponentLink';
import { MixedMedia } from '~components/MixedMedia';

import * as styles from './ComponentTextMedia.module.css';

import type { ContentfulComponentTextMedia, RichTextNodeWithAlignment, TextMediaOrder } from './types';

export const getMedia = (content: ContentfulComponentTextMedia): JSX.Element | null => {
  if (content.links?.length) {
    return (
      <>
        {content.links.map((link) => (
          <Button key={link.label} className={styles.link} element={<ComponentLink link={link} />}>
            {link.label}
          </Button>
        ))}
      </>
    );
  }

  if (content.youTubeVideoLink) {
    return (
      <div className={styles.video}>
        <YouTube youTubeVideoLink={content.youTubeVideoLink} />
      </div>
    );
  }

  if (content.media) {
    let className: string = '';
    if (content.media.file.contentType.startsWith('image')) className = styles.image;
    if (content.media.file.contentType.startsWith('video')) className = styles.video;
    return <MixedMedia media={content.media} className={className} />;
  }

  return null;
};

export const getOrderClassName = (order?: TextMediaOrder): string => {
  switch (order) {
    case 'Media, Text':
      return styles['mediaText'];
    case 'Text, Media':
    default:
      return styles['textMedia'];
  }
};

const YouTube: React.FC<{ youTubeVideoLink: string }> = ({ youTubeVideoLink }) => {
  const [show, setShow] = useState<boolean>(false);

  useEffect(() => {
    setShow(true);
  }, []);

  return show ? <ReactYouTube url={youTubeVideoLink} width='100%' height='100%' /> : null;
};

export const getCenterAlignedNodeClassNames = (nodes: RichTextNodeWithAlignment[] | null): string[] =>
  nodes === null || nodes.length === 0
    ? []
    : nodes
        .reduce((acc, node) => {
          switch (node) {
            case 'headings':
              acc.push('h2', 'h3', 'h4');
              break;
            case 'paragraphs':
              acc.push('p');
              break;
            case 'embedded block links':
              acc.push('link');
          }
          return acc;
        }, [] as string[])
        .map((className) => styles[`${className}Center`]);
